import MgnlLink from './mgnllink';
import { findAttributeRange } from 'ckeditor5/src/typing';
import type { Model, ViewDocumentFragment, ViewNode } from 'ckeditor5/src/engine';

/**
 * The Magnolia link plugin utility.
 */
export default class MgnlLinkUtil {
	public static findMgnlLinkAttributeValuesOnSelection( model: Model ): { workspace: string | undefined; path: string | undefined } {
		const selection = model.document.selection;
		// Check if the link is already selected and link contains MgnlLink.MGNL_LINK_ATTRIBUTE attribute.
		let workspace: string | undefined = undefined;
		let path: string | undefined = undefined;
		if ( selection.hasAttribute( MgnlLink.MGNL_LINK_ATTRIBUTE ) ) {
			const selected = selection.getAttribute( MgnlLink.MGNL_LINK_ATTRIBUTE );
			if ( selected && typeof selected === 'string' ) {
				// reset target workspace and path if the link is already selected.
				workspace = MgnlLinkUtil.getMgnlLinkRepository( selected );
				path = MgnlLinkUtil.getMgnlLinkPath( selected );

				const position = selection.getFirstPosition()!;
				const linkRange = findAttributeRange( position, MgnlLink.MGNL_LINK_ATTRIBUTE, selected, model );

				// change current selection to the link range.
				model.change( writer => {
					writer.setSelection( linkRange );
				} );
			}
		}
		return { workspace, path };
	}

	/**
	 * Check if the link contains the workspace same as the given workspace name
	 * @param href link to check
	 * @param workspaceToCheck workspace name to check
	 */
	public static isContainsMgnlWorkspace( href: string, workspaceToCheck: string ): boolean {
		return workspaceToCheck === MgnlLinkUtil.getMgnlLinkRepository( href );
	}

	/**
	 * Retrieves the repository from the Magnolia link.
	 * @param href Magnolia link.
	 * @private
	 */
	public static getMgnlLinkRepository( href: string ): string | undefined {
		return MgnlLinkUtil.getMgnlLinkParam( href, 'repository' );
	}

	/**
	 * Retrieves the path from the Magnolia link.
	 * @param href Magnolia link.
	 * @private
	 */
	public static getMgnlLinkPath( href: string ): string | undefined {
		return MgnlLinkUtil.getMgnlLinkParam( href, 'path' );
	}

	/**
	 * Retrieves the param value from the Magnolia link by given param name.
	 * @param href Magnolia link.
	 * @param paramName
	 * @private
	 */
	private static getMgnlLinkParam( href: string, paramName: string ): string | undefined {
		if ( !this.isMgnlLink( href ) ) {
			return undefined;
		}
		const regexp = new RegExp( `${ paramName }:{([^}]*)}` );
		const param = href.match( regexp );

		if ( param ) {
			return param[ 1 ];
		} else {
			return undefined;
		}
	}

	/**
	 * Returns `true` if the provided `ViewNode` is a mgnl link.
	 * @param node
	 */
	public static isMgnlLinkElement( node: ViewNode | ViewDocumentFragment ): boolean {
		return node.is( 'attributeElement' ) && !!node.getCustomProperty( 'link' ) &&
			this.isMgnlLink( node.getAttribute( 'href' ) as string );
	}

	/**
     * Checks if the given Magnolia link is valid.
     * @param href Magnolia link.
     * @private
     */
	public static isMgnlLink( href: string ): boolean {
		if ( href ) {
			const regexp = '^\\${link:.+';
			const match = href!.match( regexp );
			return !!match;
		}
		return false;
	}
}

